interface DsIndexed {
  _dsIdx?: number;
}

/**
 * Array with O(1) `remove`/`includes` for the half-edge structure's element
 * pools (vertices / halfedges / faces). Each element carries its current pool
 * index in a `_dsIdx` back-reference maintained by `push`/`remove`/`splice`.
 *
 * Removal swap-pops (moves the last element into the freed slot), so element
 * ITERATION ORDER IS NOT STABLE across removals. No pool consumer may rely on
 * order — `splice` never kept indices stable across removals either.
 *
 * Only `push`, `splice` and `remove` are index-safe mutators. Do not use
 * `pop`/`shift`/`unshift`/`sort`/`reverse`/direct index assignment on a pool.
 */
export class TrackedArray<T> extends Array<T> {
  // Derived arrays (filter/map/slice/…) must be plain Arrays: a tracked copy
  // sharing elements with the pool would corrupt `_dsIdx` on its own mutations.
  static get [Symbol.species]() {
    return Array;
  }

  push(...items: T[]): number {
    for (let i = 0; i < items.length; i++) {
      (items[i] as DsIndexed)._dsIdx = this.length + i;
    }
    return super.push(...items);
  }

  remove(t: T): boolean {
    const idx = (t as DsIndexed)._dsIdx;
    if (idx !== undefined && idx >= 0 && this[idx] === t) {
      this.swapPop(idx);
      return true;
    }
    // Untracked or stale back-reference — match Array.prototype.remove.
    const found = this.indexOf(t);
    if (found === -1) return false;
    this.swapPop(found);
    return true;
  }

  includes(t: T, fromIndex?: number): boolean {
    if (fromIndex === undefined) {
      const idx = (t as DsIndexed)._dsIdx;
      if (idx !== undefined) return idx >= 0 && this[idx] === t;
    }
    return super.includes(t, fromIndex);
  }

  splice(start: number, deleteCount?: number, ...items: T[]): T[] {
    const removed =
      deleteCount === undefined ? super.splice(start) : super.splice(start, deleteCount, ...items);
    for (const r of removed) (r as DsIndexed)._dsIdx = -1;
    const from = Math.max(0, start < 0 ? this.length + start : start);
    for (let i = from; i < this.length; i++) (this[i] as DsIndexed)._dsIdx = i;
    return removed;
  }

  private swapPop(idx: number): void {
    const t = this[idx];
    const last = super.pop() as T;
    if (last !== t) {
      this[idx] = last;
      (last as DsIndexed)._dsIdx = idx;
    }
    (t as DsIndexed)._dsIdx = -1;
  }
}
